import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Minus, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const ProductDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [slug]);

  if (p === null) return <div className="p-20 text-center" data-testid="loading">Loading…</div>;
  if (p === false) return (
    <div className="p-20 text-center" data-testid="not-found">
      <p className="mb-6">Product not found.</p>
      <Link to="/shop/medicines" className="btn-secondary">Browse Medicines</Link>
    </div>
  );

  const handleAdd = () => {
    addItem(p, qty);
    toast.success(`Added ${qty} × ${p.name} to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="px-6 md:px-12 lg:px-20 pt-24 pb-24">
      <Link to={`/shop/${p.category === "medicine" ? "medicines" : "equipment"}`} className="inline-flex items-center gap-2 text-sm mb-10 opacity-70 hover:opacity-100" data-testid="back-link">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-7">
          <img src={p.image} alt={p.name} className="w-full h-[70vh] object-cover" />
        </div>
        <div className="lg:col-span-5">
          <p className="overline mb-3">{p.sub_category}</p>
          <h1 className="font-display text-5xl leading-tight mb-4">{p.name}</h1>
          <p className="text-2xl mb-8" style={{ color: "var(--ayu-secondary)" }}>₹{p.price.toLocaleString("en-IN")}</p>
          <p className="leading-relaxed mb-8" style={{ color: "var(--ayu-body)" }}>{p.description}</p>

          <div className="mb-8">
            <p className="overline mb-4">Key Benefits</p>
            <ul className="space-y-2">
              {p.benefits.map((b) => (
                <li key={b} className="flex gap-3 text-sm">
                  <Check size={16} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} className="mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-6 mb-8">
            <div className="flex items-center border" style={{ borderColor: "var(--ayu-border)" }}>
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3"><Minus size={14} /></button>
              <span data-testid="qty-value" className="px-4 min-w-[40px] text-center">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="p-3"><Plus size={14} /></button>
            </div>
            <button 
              data-testid="add-to-cart-button" 
              onClick={handleAdd} 
              disabled={!p.in_stock}
              className={`btn-primary flex-1 justify-center ${!p.in_stock ? "opacity-40 cursor-not-allowed hover:bg-[var(--ayu-primary)]" : ""}`}
            >
              {p.in_stock ? "Add to Cart" : "Out of Stock"}
            </button>
          </div>

          <div className="border-t pt-6 text-xs opacity-70 leading-relaxed" style={{ borderColor: "var(--ayu-border)" }}>
            Orders are confirmed by our team within 24 hours. For consultations on the correct
            dosha/dosage, please <Link to="/contact" className="underline">contact a Vaidya</Link>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
